import { differenceInMilliseconds } from 'date-fns';

export const ONE_SECOND = 1000;
export const ONE_MINUTE = ONE_SECOND * 60;
export const ONE_HOUR = ONE_MINUTE * 60;
export const ONE_DAY = ONE_HOUR * 24;
export const ONE_WEEK = ONE_DAY * 7;

export function duration(
    duration:
        | number
        | {
              milliseconds?: number;
              seconds?: number;
              minutes?: number;
              hours?: number;
              days?: number;
          },
): number {
    if (typeof duration === 'number') {
        return duration;
    }

    return (
        (duration.milliseconds ?? 0) +
        (duration.seconds ?? 0) * ONE_SECOND +
        (duration.minutes ?? 0) * ONE_MINUTE +
        (duration.hours ?? 0) * ONE_HOUR +
        (duration.days ?? 0) * ONE_DAY
    );
}

export function getDuration(ms: number) {
    const numberOfHours = ms / (1000 * 60 * 60);
    const days = Math.floor(numberOfHours / 24);
    const remainer = numberOfHours % 24;
    const hours = Math.floor(remainer);
    const minutes = Math.floor(60 * (remainer - hours));
    return { days: days, hours: hours, minutes: minutes };
}

export function getRemainingTime(end: Date | undefined | null) {
    if (!end) {
        return {};
    }
    const now = new Date();
    const ms = end?.getTime() - now.getTime();
    const { days: remainingDays, hours: remainingHours } = getDuration(ms);
    return { remainingDays, remainingHours };
}

type DurationInput = {
    weeks?: number;
    days?: number;
    hours?: number;
    minutes?: number;
    seconds?: number;
    milliseconds?: number;
};
const DURATION_UNITS = ['weeks', 'days', 'hours', 'minutes', 'seconds', 'milliseconds'] as const satisfies (keyof DurationInput)[];

export type GetAbsoluteDurationBetweenDatesOptions = {
    smallestUnitToInclude: keyof DurationInput;
    shouldTrimZeroes: boolean;
};
const defaultGetAbsoluteDurationBetweenDatesOptions: GetAbsoluteDurationBetweenDatesOptions = {
    smallestUnitToInclude: 'seconds',
    shouldTrimZeroes: true,
};

/** Get absolute duration between two dates/timestamps */
export function getAbsoluteDurationBetweenDates(
    a: Date | number,
    b: Date | number,
    options: Partial<GetAbsoluteDurationBetweenDatesOptions> = {},
): DurationInput {
    const { smallestUnitToInclude, shouldTrimZeroes } = Object.assign({}, defaultGetAbsoluteDurationBetweenDatesOptions, options);

    const timestampA = typeof a === 'number' ? a : a.getTime();
    const timestampB = typeof b === 'number' ? b : b.getTime();

    const timestampStart = timestampA < timestampB ? timestampA : timestampB;
    const timestampEnd = timestampStart === timestampA ? timestampB : timestampA;

    const totalMilliseconds = differenceInMilliseconds(timestampEnd, timestampStart);

    const weeks = Math.floor(totalMilliseconds / ONE_WEEK);
    const remainingAfterWeeks = totalMilliseconds - weeks * ONE_WEEK;

    const days = Math.floor(remainingAfterWeeks / ONE_DAY);
    const remainingAfterDays = remainingAfterWeeks - days * ONE_DAY;

    const hours = Math.floor(remainingAfterDays / ONE_HOUR);
    const remainingAfterHours = remainingAfterDays - hours * ONE_HOUR;

    const minutes = Math.floor(remainingAfterHours / ONE_MINUTE);
    const remainingAfterMinutes = remainingAfterHours - minutes * ONE_MINUTE;

    const seconds = Math.floor(remainingAfterMinutes / ONE_SECOND);
    const milliseconds = remainingAfterMinutes - seconds * ONE_SECOND;

    const result: DurationInput = {
        weeks,
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
    };

    if (shouldTrimZeroes) {
        for (const key of DURATION_UNITS) {
            if (result[key] === 0) {
                delete result[key];
            }
        }
    }

    if (smallestUnitToInclude !== 'milliseconds') {
        const smallestUnitIndex = DURATION_UNITS.indexOf(smallestUnitToInclude);
        if (smallestUnitIndex !== -1) {
            const unitsToDelete = DURATION_UNITS.slice(smallestUnitIndex + 1);
            for (const unit of unitsToDelete) {
                delete result[unit];
            }
        }
    }

    if (Object.keys(result).length === 0) {
        result[smallestUnitToInclude] = 0;
    }

    return result;
}
